import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import './Page.css';
import { fetchUpcomingEvents } from '../services/calendarService';
import usePageTitle from '../hooks/usePageTitle';

function Calendar() {
  usePageTitle('Lodge Calendar');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadEvents = async () => {
      const upcomingEvents = await fetchUpcomingEvents(3);
      setEvents(upcomingEvents);
      setLoading(false);
    };
    loadEvents();
  }, []);

  const getEventDateParts = (event) => {
    const dateString = event.start.dateTime || event.start.date;
    const date = new Date(dateString);
    
    return {
      month: date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(),
      day: date.getDate()
    };
  };

  const getEventTime = (event) => {
    if (!event.start.dateTime) return 'All Day';
    
    const startDate = new Date(event.start.dateTime);
    const endDate = event.end.dateTime ? new Date(event.end.dateTime) : null;
    
    const startTime = startDate.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    
    if (endDate) {
      const endTime = endDate.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
      return `${startTime} - ${endTime}`;
    }
    
    return startTime;
  };

  const getEventLocation = (event) => {
    return event.location || null;
  };

  return (
    <div className="page-container">
      <div className="page-hero calendar-hero" style={{ backgroundImage: 'url(/squareandcompasses2.jpg)' }}>
        <h1>Lodge Calendar</h1>
        <p>Upcoming Events & Meetings</p>
      </div>

      <div className="page-content">
        <section className="content-section">
          <h2>Upcoming Events</h2>
          
          {loading ? (
            <p style={{ textAlign: 'center', padding: '2rem' }}>Loading events...</p>
          ) : events.length > 0 ? (
            <div className="events-list">
              {events.map((event, index) => {
                const dateParts = getEventDateParts(event);
                return (
                  <div key={event.id || index} className="event-item lodge-event">
                    <div className="event-date-badge">
                      <span className="month">{dateParts.month}</span>
                      <span className="day">{dateParts.day}</span>
                    </div>
                    <div className="event-details">
                      <div className="event-header">
                        <h3>{event.summary || 'Event'}</h3>
                        <span className="event-time">{getEventTime(event)}</span>
                      </div>
                      {getEventLocation(event) && (
                        <p className="event-location">{getEventLocation(event)}</p>
                      )}
                      {event.description && (
                        <div 
                          className="event-description"
                          dangerouslySetInnerHTML={{ 
                            __html: DOMPurify.sanitize(
                              event.description
                                .replace(/\n/g, '<br />')
                                .replace(/\* (.*?)(?=\n|$)/g, '<li>$1</li>')
                                .replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>')
                                .replace(
                                  /(?<!href=["'])(?<!>)(https?:\/\/[^\s<]+)/g,
                                  '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>'
                                ),
                              { 
                                ALLOWED_TAGS: ['br', 'ul', 'li', 'b', 'i', 'strong', 'em', 'p', 'a'],
                                ALLOWED_ATTR: ['href', 'target', 'rel']
                              }
                            )
                          }}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p style={{ textAlign: 'center', padding: '2rem' }}>No upcoming events scheduled. Check back soon!</p>
          )}
        </section>

        <section className="content-section">
          <h2>Full Calendar</h2>
          <div className="calendar-embed">
            <iframe
              src="https://calendar.google.com/calendar/embed?src=0cbd541e5edf46c776e1c37238686fbf8ec6a133d5740b5138e725792943439a%40group.calendar.google.com&ctz=America%2FNew_York"
              style={{ border: 0, borderRadius: '8px' }}
              width="100%"
              height="600"
              frameBorder="0"
              scrolling="no"
              title="Lodge Calendar"
            ></iframe>
          </div>
        </section>

        <section className="content-section">
          <h2>18th Masonic District</h2>
          <p>View stated meeting schedules for all lodges in the 18th Masonic District.</p>
          <p style={{ marginTop: '1rem' }}>
            <Link to="/district" className="district-link">View District Schedule &rarr;</Link>
          </p>
        </section>
      </div>
    </div>
  );
}

export default Calendar;
